// Presets y catálogos para el Setup Wizard médico de Consultame (NER-3832).
// Consultame reutiliza el admin de Citame con `vertical=medical` (Opción A,
// NER-3656). Fuente de verdad del flujo de 8 pasos + coberturas + compliance:
// whatsapp-scheduler/.agents/citame/product/medical-product-spec.md (§Fase 2).
//
// El backend expone `GET /admin/consultame/coverage-master` como fuente
// autoritativa del catálogo de obras sociales; COVERAGE_MASTER_FALLBACK se usa
// sólo si ese endpoint no responde (degradación offline / backend aún no live).

export const MEDICAL_VERTICAL = 'medical';

// DPA (Data Processing Agreement) — spec §Compliance P0.2. Versionado para poder
// mostrar qué versión aceptó el profesional. Editable en el wizard/admin.
export const DPA_VERSION = '2026-08-provisional';

export const DPA_DEFAULT_TEXT = `ACUERDO DE PROCESAMIENTO DE DATOS (DPA) — Consultame

1. Roles. El profesional/consultorio es el RESPONSABLE de los datos personales de
   sus pacientes. Nerdo Solutions actúa como ENCARGADO del tratamiento, procesando
   los datos únicamente para prestar el servicio de gestión de turnos.

2. Marco legal. Este acuerdo se rige por la Ley 25.326 de Protección de Datos
   Personales de la República Argentina. Los datos de salud son datos sensibles.

3. Finalidad. Los datos (nombre, teléfono, obra social, turnos) se tratan
   exclusivamente para agendar, recordar y gestionar turnos. No se comparten con
   terceros ni con marketplaces.

4. Derechos ARCO. El paciente puede ejercer sus derechos de Acceso, Rectificación,
   Cancelación y Oposición ante el consultorio responsable.

5. Vigencia. Acuerdo provisional para el piloto. Se reemplazará por la versión
   definitiva antes del lanzamiento general.`;

// Plantilla de aviso de consentimiento al paciente en el primer contacto con el
// bot de WhatsApp — spec §Compliance P0.1. Placeholders reemplazables.
export const PATIENT_CONSENT_DEFAULT_TEMPLATE =
  'Al continuar aceptás que {{consultorio}} usa Consultame para gestionar tus ' +
  'turnos. Tus datos no se comparten con terceros. Podés solicitar acceso, ' +
  'rectificación o eliminación a {{email}}.';

// Tokens disponibles para la plantilla de consentimiento del paciente.
export const PATIENT_CONSENT_TOKENS = ['{{consultorio}}', '{{email}}'];

// Especialidades comunes de salud en AR — spec §Step 2. `otro` habilita texto
// libre. `slotDurationMinutes` alimenta el default del preset por especialidad.
export const SPECIALTIES = [
  { key: 'medicina_general', label: 'Medicina General', slotDurationMinutes: 20 },
  { key: 'cardiologia', label: 'Cardiología', slotDurationMinutes: 30 },
  { key: 'psicologia', label: 'Psicología', slotDurationMinutes: 50 },
  { key: 'kinesiologia', label: 'Kinesiología', slotDurationMinutes: 40 },
  { key: 'odontologia', label: 'Odontología', slotDurationMinutes: 30 },
  { key: 'traumatologia', label: 'Traumatología', slotDurationMinutes: 25 },
  { key: 'ginecologia', label: 'Ginecología', slotDurationMinutes: 30 },
  { key: 'pediatria', label: 'Pediatría', slotDurationMinutes: 25 },
  { key: 'otro', label: 'Otro', slotDurationMinutes: 30 },
];

// Provincias AR para la matrícula provincial (MedicalLicenseInput).
export const AR_PROVINCES = [
  'Buenos Aires',
  'CABA',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán',
];

// Catálogo de obras sociales — fallback si el backend no responde. `particular`
// siempre activo y no removible; `otra` permite agregar coberturas custom.
export const COVERAGE_MASTER_FALLBACK = [
  { key: 'ioma', name: 'IOMA' },
  { key: 'osde', name: 'OSDE' },
  { key: 'pami', name: 'PAMI' },
  { key: 'swiss_medical', name: 'Swiss Medical' },
  { key: 'galeno', name: 'Galeno' },
  { key: 'medife', name: 'Medifé' },
  { key: 'omint', name: 'OMINT' },
  { key: 'osecac', name: 'OSECAC' },
  { key: 'ospaca', name: 'OSPACA' },
];

// Tipos de consulta por defecto — spec §Step 5.
export const CONSULTATION_TYPES_DEFAULT = [
  { key: 'primera', name: 'Primera consulta', durationMin: 40, priceArs: '', whatsappEnabled: true },
  { key: 'control', name: 'Consulta de control', durationMin: 20, priceArs: '', whatsappEnabled: true },
  { key: 'urgencia', name: 'Urgencia', durationMin: 15, priceArs: '', whatsappEnabled: false },
];

const DEFAULT_TZ = 'America/Argentina/Buenos_Aires';

// Cobertura "Particular" — siempre presente, activa y no removible.
function particularCoverage() {
  return { key: 'particular', name: 'Particular', enabled: true, locked: true, copayArs: '' };
}

// Construye la lista inicial de coberturas para el wizard a partir del catálogo
// master. `defaultActiveKeys` (ej. IOMA si provincia=Buenos Aires) arranca ON.
export function coverageMasterToState(master, defaultActiveKeys = []) {
  const list = (master && master.length ? master : COVERAGE_MASTER_FALLBACK).map((c) => ({
    key: c.key,
    name: c.name,
    enabled: defaultActiveKeys.includes(c.key),
    locked: false,
    copayArs: '',
  }));
  return [...list, particularCoverage()];
}

// Estado inicial del wizard médico. Toma el preset de especialidad para el slot
// default; el resto arranca vacío salvo compliance defaults.
export function medicalInitialState() {
  return {
    vertical: MEDICAL_VERTICAL,
    compliance: {
      termsAccepted: false,
      dpaAccepted: false,
      dpaVersion: DPA_VERSION,
      dpaText: DPA_DEFAULT_TEXT,
    },
    professional: {
      fullName: '',
      email: '',
      specialty: '',
      specialtyOther: '',
      license: '',
      licenseProvince: '',
      cuil: '',
    },
    clinic: { name: '', address: '', phone: '', city: '', province: '' },
    coverages: coverageMasterToState(COVERAGE_MASTER_FALLBACK),
    consultationTypes: CONSULTATION_TYPES_DEFAULT.map((c) => ({ ...c })),
    hours: {
      days: [1, 2, 3, 4, 5],
      openTime: '09:00',
      closeTime: '18:00',
      customPerDay: null,
    },
    whatsapp: {
      number: '',
      privacyNoticeEnabled: true,
      acceptsUrgencies: false,
      patientConsentTemplate: PATIENT_CONSENT_DEFAULT_TEMPLATE,
    },
    settings: { timezone: DEFAULT_TZ },
  };
}

// Devuelve el slot default (min) para una especialidad dada.
export function slotForSpecialty(specialtyKey) {
  const s = SPECIALTIES.find((x) => x.key === specialtyKey);
  return s ? s.slotDurationMinutes : 30;
}
